import React from 'react';

/**
 * CoRegulate Logo - Sunset Lake Design
 * 
 * Primary: Deep Teal (#1D6B6B) + Warm Peach (#FFBB98)
 * Symbolism: Two nervous systems gently meeting and co-regulating
 */

// Logo Mark Only (Icon) - with gradient and depth
export const CoRegulateMark = ({ 
    size = 48, 
    primaryColor = '#1D6B6B',  // Deep Teal
    secondaryColor = '#FFBB98', // Warm Peach
    className = '' 
}) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 48 48" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="CoRegulate logo mark"
    >
        <defs>
            {/* Teal gradient - dark ends, lighter middle */}
            <linearGradient id="tealGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#145555" />
                <stop offset="50%" stopColor="#3A9595" />
                <stop offset="100%" stopColor="#1D6B6B" />
            </linearGradient>
            {/* Peach gradient - dark ends, lighter middle */}
            <linearGradient id="peachGradient" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#D07848" />
                <stop offset="50%" stopColor="#FFAA78" />
                <stop offset="100%" stopColor="#E89060" />
            </linearGradient>
            {/* Shadow filter */}
            <filter id="logoShadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#1D6B6B" floodOpacity="0.3"/>
            </filter>
        </defs>
        {/* Two curves representing co-regulation */}
        <path 
            d="M12 36C12 36 14 20 24 14C28 11.5 32 12 32 12" 
            stroke="url(#tealGradient)"
            strokeWidth="4.5" 
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            filter="url(#logoShadow)"
        />
        <path 
            d="M36 36C36 36 34 20 24 14C20 11.5 16 12 16 12" 
            stroke="url(#peachGradient)"
            strokeWidth="4.5" 
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            filter="url(#logoShadow)"
        />
    </svg>
);

// Animated Logo Mark - Hero Version with gradient and depth
export const CoRegulateMarkAnimated = ({ 
    size = 56, 
    primaryColor = '#1D6B6B',
    secondaryColor = '#FFBB98',
    className = '' 
}) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 48 48" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="CoRegulate logo"
    >
        <defs>
            {/* Teal gradient - dark ends, lighter middle */}
            <linearGradient id="tealGradientHero" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#145555" />
                <stop offset="50%" stopColor="#3A9595" />
                <stop offset="100%" stopColor="#1D6B6B" />
            </linearGradient>
            {/* Peach gradient - dark ends, lighter middle */}
            <linearGradient id="peachGradientHero" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#D07848" />
                <stop offset="50%" stopColor="#FFAA78" />
                <stop offset="100%" stopColor="#E89060" />
            </linearGradient>
            {/* Shadow filter for depth */}
            <filter id="heroShadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#1D6B6B" floodOpacity="0.3"/>
            </filter>
        </defs>
        <path 
            d="M12 36C12 36 14 20 24 14C28 11.5 32 12 32 12" 
            stroke="url(#tealGradientHero)"
            strokeWidth="4.5" 
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            filter="url(#heroShadow)"
        />
        <path 
            d="M36 36C36 36 34 20 24 14C20 11.5 16 12 16 12" 
            stroke="url(#peachGradientHero)"
            strokeWidth="4.5" 
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            filter="url(#heroShadow)"
        />
    </svg>
);

// Full Logo with Icon + Text (like "Find Your Calm" style)
export const CoRegulateLogo = ({ 
    size = 'md',
    showTagline = false,
    className = '' 
}) => {
    const sizes = {
        xs: { mark: 28, text: 18, gap: 8, tagline: 10 },
        sm: { mark: 36, text: 22, gap: 10, tagline: 11 },
        md: { mark: 44, text: 28, gap: 12, tagline: 12 },
        lg: { mark: 52, text: 34, gap: 14, tagline: 14 },
        xl: { mark: 64, text: 42, gap: 16, tagline: 16 }
    };
    
    const s = sizes[size] || sizes.md;
    
    return (
        <div className={`flex items-center ${className}`} style={{ gap: s.gap }}>
            <CoRegulateMark size={s.mark} />
            <div className="flex flex-col">
                <span 
                    style={{ 
                        fontFamily: "'Lora', Georgia, serif",
                        fontSize: s.text,
                        fontWeight: 500,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.1
                    }}
                >
                    <span className="gradient-text-teal">Co</span>
                    <span className="gradient-text-peach">Regulate</span>
                </span>
                {showTagline && (
                    <span 
                        style={{ 
                            fontFamily: "'Inter', sans-serif",
                            fontSize: s.tagline,
                            color: '#7D8E95',
                            marginTop: 2,
                            fontWeight: 400
                        }}
                    >
                        Made for ADHD hearts, shaped by ADHD minds.
                    </span>
                )}
            </div>
        </div>
    );
};

// Logo Mark alternatives for backward compatibility
export const CoRegulateMarkLoops = CoRegulateMark;
export const CoRegulateMarkCircles = CoRegulateMark;

// Favicon/App Icon version - with gradient
export const CoRegulateFavicon = ({ 
    size = 32,
    primaryColor = '#1D6B6B',
    secondaryColor = '#FFBB98'
}) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 32 32" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="tealGradientFav" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3A9595" />
                <stop offset="100%" stopColor="#145555" />
            </linearGradient>
            <linearGradient id="peachGradientFav" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFD0B0" />
                <stop offset="100%" stopColor="#E09060" />
            </linearGradient>
        </defs>
        <rect width="32" height="32" rx="8" fill="#FBE0C3" />
        <path 
            d="M8 24C8 24 10 14 16 10C19 8 22 8.5 22 8.5" 
            stroke="url(#tealGradientFav)"
            strokeWidth="3" 
            strokeLinecap="round"
            fill="none"
        />
        <path 
            d="M24 24C24 24 22 14 16 10C13 8 10 8.5 10 8.5" 
            stroke="url(#peachGradientFav)"
            strokeWidth="3" 
            strokeLinecap="round"
            fill="none"
        />
    </svg>
);

export default CoRegulateLogo;
